import { safeLocalStorage } from '@/lib/safeStorage';
import React from 'react';
import {useNavigate, useSearchParams} from 'react-router-dom';
import { API_ERROR_CODES, EMAIL_SIGN_IN_CODE_LENGTH } from '@ruta/shared';
import {forgotPassword, loginUser, requestEmailSignInCode, storeTokens, verifyEmailSignInCode} from '../../lib/api';
import {STORAGE_KEYS, getRutaWebsiteUrl} from '../../lib/constants';
import { showDemoLogins } from '../../lib/appEnvironment';
import {Card, CardContent} from '../../components/ui/card';
import {Button} from '../../components/ui/button';
import {Input} from '../../components/ui/input';
import {Label} from '../../components/ui/label';
import { ArrowLeft, CircleNotch, Lock, Envelope } from '@phosphor-icons/react';
import {usePageTitle} from '../../hooks/usePageTitle';
import { useToast } from '../../components/ui/use-toast';
import { GreenValleyQuickLogin } from './GreenValleyQuickLogin';
import { SocialSignInButtons } from '../../components/auth/SocialSignInButtons';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '../../components/ui/input-otp';

/**
 * `email-code`: "Email me a code" (fast signup). An owner who signed up with
 * only an email never chose a password, so the login page offers an 8-digit
 * code sent to that email beside the password form.
 */
type AuthMode = 'email-login' | 'forgot-password' | 'email-code';

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { toast } = useToast();
  const [mode, setMode] = React.useState<AuthMode>('email-login');
  const [isTransitioning, setIsTransitioning] = React.useState(false);
  
  usePageTitle('Login');

  // Email login state
  const [email, setEmail] = React.useState(searchParams.get('email') || '');
  const [password, setPassword] = React.useState('');
  // Emailed sign-in code: the session the API handed back with it, and the code typed.
  const [codeSession, setCodeSession] = React.useState('');
  const [code, setCode] = React.useState('');

  // Common state
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);

  // After email confirmation, show a persistent toast so users know they can log in
  React.useEffect(() => {
    const emailConfirmed = searchParams.get('emailConfirmed') === '1';
    const message = searchParams.get('message');

    if (emailConfirmed) {
      toast({
        title: 'Email confirmed',
        description:
          'Your email has been confirmed. Log in with the password you set when you first registered.',
        variant: 'success',
        duration: Infinity,
      });
    } else if (message) {
      // Generic success messages still show inline on the form
      setSuccess(decodeURIComponent(message));
    }

    if (emailConfirmed || message) {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('emailConfirmed');
      newSearchParams.delete('message');
      const newUrl = `${window.location.pathname}${newSearchParams.toString() ? `?${newSearchParams.toString()}` : ''}`;
      window.history.replaceState({}, '', newUrl);
    }
    // Only run on initial mount for the arrival query params
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for pending registration
  const checkPendingRegistration = () => {
    try {
      const saved = safeLocalStorage.getItem('ruta_registration_state');
      if (saved) {
        const registrationState = JSON.parse(saved);
        return registrationState.step === 'confirm' && registrationState.registrationCompleted;
      }
    } catch {
      // Ignore errors
    }
    return false;
  };

  const getPendingRegistrationEmail = () => {
    try {
      const saved = safeLocalStorage.getItem('ruta_registration_state');
      if (saved) {
        const registrationState = JSON.parse(saved);
        return registrationState.email;
      }
    } catch {
      // Ignore errors
    }
    return '';
  };

  const hasPendingRegistration = checkPendingRegistration();
  const pendingEmail = getPendingRegistrationEmail();


  async function signIn(loginEmail: string, loginPassword: string) {
    await finishSignIn(() => loginUser(loginEmail, loginPassword));
  }

  /** Either way in — password or emailed code — ends with the same tokens. */
  async function finishSignIn(attempt: () => ReturnType<typeof loginUser>) {
    setError(null);
    setLoading(true);
    try {
      const res = await attempt();

      const authResult = res.result.AuthenticationResult;
      if (!authResult?.IdToken || !authResult?.AccessToken || !authResult?.RefreshToken) throw new Error('Missing tokens');

      // Clear the logout flag since the user is now logged in
      safeLocalStorage.removeItem(STORAGE_KEYS.HAS_LOGGED_OUT);

      storeTokens({
        idToken: authResult.IdToken,
        accessToken: authResult.AccessToken,
        refreshToken: authResult.RefreshToken,
      });

      // Navigate to root and let RoleBasedRouter handle redirection
      navigate('/');
    } catch (error: any) {
      // Provide user-friendly error messages
      let errorMessage = 'Oops! That email or password doesn\'t look right. Please try again.';

      if (error?.code === API_ERROR_CODES.USE_CREW_APP) {
        errorMessage =
          error.message ||
          'This account uses the Ruta Crew app. Download the app and sign in with your phone number.';
      } else if (error.message) {
        // Use the specific error message from the API
        errorMessage = error.message;
      } else if (error.name === 'TypeError' && error.message?.includes('fetch')) {
        errorMessage = 'Looks like we\'re having trouble connecting. Check your internet and try again!';
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  async function handleEmailLogin(e: React.FormEvent) {
    e.preventDefault();
    await signIn(email, password);
  }

  /** "Email me a code": send it, then ask for it on the same card. */
  async function sendSignInCode() {
    const address = email.trim();
    setError(null);
    setSuccess(null);
    if (!address) {
      setError('Enter your email first, then we can send you a code.');
      return;
    }
    setLoading(true);
    try {
      const sent = await requestEmailSignInCode(address);
      setCodeSession(sent.session);
      setCode('');
      if (mode !== 'email-code') switchMode('email-code');
      setSuccess(`If ${address} has a Ruta account, an 8-digit code is on its way.`);
    } catch (err: any) {
      setError(err?.message || "We couldn't send a code. Try again in a moment.");
    } finally {
      setLoading(false);
    }
  }

  async function handleCodeLogin(e: React.FormEvent) {
    e.preventDefault();
    if (code.length !== EMAIL_SIGN_IN_CODE_LENGTH) return;
    await finishSignIn(() => verifyEmailSignInCode(email.trim(), code, codeSession));
  }


  async function handleForgotPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    setLoading(true);

    try {
      await forgotPassword(email);
      setSuccess('Check your email! We\'ve sent you a password reset link.');
      // After successful request, redirect to reset page with email prefilled
      setTimeout(() => {
        navigate(`/reset-password?email=${encodeURIComponent(email)}`);
      }, 2000);
    } catch (err: any) {
      setError(err.message || 'Hmm, we couldn\'t send that email. Please try again in a moment.');
    } finally {
      setLoading(false);
    }
  }

  function resetState() {
    setError(null);
    setSuccess(null);
  }

  function switchMode(newMode: AuthMode) {
    setIsTransitioning(true);
    resetState();

    // Add a small delay for the slide animation
    setTimeout(() => {
      setMode(newMode);
      setIsTransitioning(false);
    }, 150);
  }

  function navigateToOnboarding() {
    // Navigate immediately without animation since we're going to a different page
    navigate('/onboarding');
  }

  const getTitle = () => {
    switch (mode) {
      case 'email-login': return 'Welcome Back!';
      case 'forgot-password': return 'Reset Your Password';
      case 'email-code': return 'Check your email';
      default: return 'Welcome Back!';
    }
  };

  const getDescription = () => {
    switch (mode) {
      case 'email-login': return '';
      case 'forgot-password': return 'No worries! We\'ll send you a reset link right away';
      default: return '';
    }
  };

  const renderBackButton = () => {
    if (mode === 'email-login') return null;

    const handleBack = () => {
      switchMode('email-login');
    };

    return (
      <Button
        type="button"
        variant="link"
        onClick={handleBack}
        className="mb-4 p-0 h-auto"
      >
        <ArrowLeft className="h-4 w-4 mr-2" />
        Back
      </Button>
    );
  };

  const renderForm = () => {
    const formContent = (() => {
      switch (mode) {
        case 'email-login':
          return (
            <form onSubmit={handleEmailLogin} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-stone-700 mb-1.5">Email</Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>
              <div>
                <Label htmlFor="password" className="text-sm font-medium text-stone-700 mb-1.5">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="password"
                    type="password"
                    autoComplete="current-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={loading}>
                {loading ? (
                  <CircleNotch className="h-5 w-5 animate-spin mr-2" />
                ) : null}
                {loading ? 'Logging you in...' : 'Log In'}
              </Button>
              <Button
                type="button"
                variant="secondary"
                className="w-full h-12 text-base font-semibold"
                disabled={loading}
                onClick={sendSignInCode}
              >
                Email me a code
              </Button>
            </form>
          );

        case 'email-code':
          return (
            <form onSubmit={handleCodeLogin} className="space-y-6">
              <div>
                <Label htmlFor="signInCode" className="text-sm font-medium text-stone-700 mb-1.5">
                  Sign-in code sent to {email.trim()}
                </Label>
                <InputOTP id="signInCode" maxLength={EMAIL_SIGN_IN_CODE_LENGTH} value={code} onChange={setCode} autoFocus>
                  <InputOTPGroup>
                    {Array.from({ length: EMAIL_SIGN_IN_CODE_LENGTH }, (_, index) => (
                      <InputOTPSlot key={index} index={index} className="h-14 w-12 border-stone-300 text-xl" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
                <p className="mt-3 text-sm text-muted-foreground">
                  It works once and expires in a few minutes. Not there? Check spam, or{' '}
                  <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={sendSignInCode} disabled={loading}>
                    send a new code
                  </Button>
                  .
                </p>
              </div>
              <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={loading || code.length !== EMAIL_SIGN_IN_CODE_LENGTH}>
                {loading ? <CircleNotch className="h-5 w-5 animate-spin mr-2" /> : null}
                {loading ? 'Logging you in...' : 'Log In'}
              </Button>
            </form>
          );


        case 'forgot-password':
          return (
            <form onSubmit={handleForgotPasswordSubmit} className="space-y-6">
              <div>
                <Label htmlFor="forgotEmail" className="text-sm font-medium text-stone-700 mb-1.5">Email Address</Label>
                <div className="relative">
                  <Envelope className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    id="forgotEmail"
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="you@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="h-12 text-base pl-10"
                  />
                </div>
              </div>
              <Button type="submit" className="w-full h-12 text-base font-semibold" disabled={loading}>
                {loading ? (
                  <CircleNotch className="h-5 w-5 animate-spin mr-2" />
                ) : null}
                {loading ? 'Sending reset link...' : 'Send Reset Link'}
              </Button>
            </form>
          );

        default:
          return null;
      }
    })();

    return (
      <div className={`transition-all duration-300 ease-in-out ${isTransitioning ? 'transform translate-x-full opacity-0' : 'transform translate-x-0 opacity-100'}`}>
        {formContent}
      </div>
    );
  };

  const renderFooterLinks = () => {
    switch (mode) {
      case 'email-login':
        return (
          <>
            <Button
              type="button"
              variant="link"
              onClick={() => switchMode('forgot-password')}
              className="text-base p-0 h-auto"
            >
              Forgot your password?
            </Button>
            {hasPendingRegistration && (
              <div className="mt-4 p-4 bg-primary/5 border border-primary/20 rounded-lg text-center">
                <p className="text-sm text-primary mb-2">
                  You started signing up with <strong>{pendingEmail}</strong> — want to finish?
                </p>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => navigate(`/onboarding?step=confirm&email=${encodeURIComponent(pendingEmail)}`)}
                  className="text-sm font-medium"
                >
                  Complete your registration →
                </Button>
              </div>
            )}
            <div className="mt-4 text-center">
              <span className="text-base text-muted-foreground">Don't have an account? </span>
              <Button
                type="button"
                variant="link"
                onClick={() => navigateToOnboarding()}
                className="text-base font-medium h-auto p-0"
              >
                Create one here
              </Button>
            </div>
          </>
        );


      case 'forgot-password':
        return (
          <Button
            type="button"
            variant="ghost"
            onClick={() => switchMode('email-login')}
            className="text-base font-medium"
          >
            Remember your password? Log in
          </Button>
        );

      case 'email-code':
        return (
          <Button
            type="button"
            variant="ghost"
            onClick={() => switchMode('email-login')}
            className="text-base font-medium"
          >
            Use my password instead
          </Button>
        );

      default:
        return null;
    }
  };



  return (
    <div className="min-h-screen bg-nav flex flex-col relative overflow-hidden">
      {/* Glow effect */}
      <div className="absolute inset-0 bg-gradient-radial from-primary/10 via-transparent to-transparent" style={{backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(34, 197, 94, 0.15) 0%, transparent 50%)'}} />
      <div className="absolute inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent" style={{backgroundImage: 'radial-gradient(circle at 20% 80%, rgba(34, 197, 94, 0.1) 0%, transparent 40%)'}} />
      <div className="absolute inset-0 bg-gradient-radial from-primary/5 via-transparent to-transparent" style={{backgroundImage: 'radial-gradient(circle at 80% 20%, rgba(34, 197, 94, 0.1) 0%, transparent 40%)'}} />
      <div className="flex-1 flex flex-col items-center justify-center p-4 relative z-10">
        <div className="w-full max-w-xl">
          {/* Header outside card */}
          <div className="text-center mb-6 text-white animate-in fade-in slide-in-from-top-4 duration-500">
            <h1 className="text-4xl font-bold mb-2 tracking-tight">
              {getTitle()}
            </h1>
          </div>
          
          <div className="space-y-4">
          <Card className="animate-in fade-in slide-in-from-bottom-4 duration-500">
            <CardContent>

            {/* Back button */}
            {renderBackButton()}
            
            {error && <div className="mb-6 p-4 bg-destructive/10 text-destructive rounded-lg text-sm">{error}</div>}
            {success && <div className="mb-6 p-4 bg-green-50 text-green-600 rounded-lg text-sm">{success}</div>}

            {mode === 'email-login' && <SocialSignInButtons intent={{ kind: 'login' }} className="mb-6" />}

            {renderForm()}

            <div className="flex flex-col items-center gap-4 mt-8">
              {renderFooterLinks()}
            </div>
            </CardContent>
          </Card>

          {mode === 'email-login' &&
            showDemoLogins(
              window.location.hostname,
              import.meta.env.VITE_ENVIRONMENT,
              import.meta.env.VITE_API_URL,
            ) && (
              <GreenValleyQuickLogin
                loading={loading}
                onSelect={(staff) => signIn(staff.email, staff.password)}
              />
            )}
          </div>
        </div>
      </div>
      
      {/* Footer */}
      <div className="text-center pb-6 relative z-10">
        <a href={getRutaWebsiteUrl()} target="_blank" rel="noopener noreferrer" className="inline-block hover:opacity-80 transition-opacity">
          <p className="text-white/70 text-[10px] uppercase tracking-wider">Powered by</p>
          <p className="text-white font-black text-2xl tracking-wider">RUTA</p>
        </a>
        <p className="text-white/40 text-xs mt-2">
          v{import.meta.env.VITE_APP_VERSION}
        </p>
      </div>
    </div>
  );
}
